import {
  addBreadcrumb,
  Breadcrumb,
  captureException as sentryCaptureException,
  captureMessage as sentryCaptureMessage,
  SeverityLevel,
  withScope,
} from '@sentry/react';

import ApiNetworkError from 'lib/errors/ApiNetworkError';
import ApiResponseError from 'lib/errors/ApiResponseError';

/**
 * Specifies some common tag names for consistency.
 * Blacklists keys we set at the application level to prevent unintended overwriting.
 */
type SentryTag = {
  [key: string]: string | undefined;
  // Allow any key, but specify these for better autocomplete / consistency.
  jira?: `PER-${number}`;
  functionName?: string;
  // These are set at the app level; don't overwrite
  /**
   * Type of the current environment, NOT staging / production.
   */
  environment_type?: never;
  organization_id?: never;
  organization_slug?: never;
  viewType?: never;
};

export function captureMessage(
  message: string,
  level: SeverityLevel = 'error',
  errorInfo: { [key: string]: any } = {},
  /**
   * Tag keys have a maximum length of 32 characters
   * Tag values have a maximum length of 200 characters
   */
  tags: SentryTag = {},
  breadcrumbs: Breadcrumb[] = [],
) {
  switch (import.meta.env.MODE) {
    case 'test':
      break;
    case 'production': {
      withScope((scope) => {
        scope.setExtras(errorInfo);
        scope.setLevel(level);
        scope.setTags(tags);

        for (const b of breadcrumbs) {
          scope.addBreadcrumb(b);
        }

        sentryCaptureMessage(message);
      });

      break;
    }
    case 'development':
      console.warn('Captured message', message, errorInfo, breadcrumbs);
      if (level === 'error') {
        throw new Error(message);
      }
  }
}

export function captureException(
  error: unknown,
  level: SeverityLevel = 'error',
  errorInfo: { [key: string]: any } = {},
  /**
   * Tag keys have a maximum length of 32 characters
   * Tag values have a maximum length of 200 characters
   */
  tags: SentryTag = {},
) {
  switch (import.meta.env.MODE) {
    case 'test':
      break;
    case 'production': {
      withScope((scope) => {
        scope.setExtras(errorInfo);
        scope.setLevel(level);
        scope.setTags(tags);

        if (error instanceof ApiResponseError) {
          // Blocked by WAF
          if (error.status === 406 || error.status === 503) return;

          // 429s are various rate limit related errors. we generally don't care.
          if (error.status === 429) return;

          // We do care about 500s, but the server will alert with more actionable information.
          // No need to double-alert.
          if (error.status === 500) return;

          // Remove IDs from member routes
          const requestUri = error.uri?.split('/')?.[0];

          scope.setTags({
            errorName: error.name,
            requestMethod: error.method,
            requestUri,
            responseStatus: String(error.status),
          });

          // Stop all axios errors from being fingerprinted as handleAxiosError
          scope.setFingerprint([
            'api-response-error',
            error.name,
            error.method ?? 'no-method',
            requestUri,
            String(error.status),
          ]);

          // This is the actual server error JSON payload
          const firstError = error.errors?.[0];
          if (firstError != null) {
            scope.setExtras({
              errorCode: firstError.code,
              errorDetails: firstError.details,
              errorTitle: firstError.title,
            });
          }
        } else if (error instanceof ApiNetworkError) {
          // Remove IDs from member routes
          const requestUri = error.uri?.split('/')?.[0];

          scope.setTags({
            errorName: error.name,
            requestMethod: error.method,
            requestUri,
          });

          // Stop all axios errors from being fingerprinted as handleAxiosError
          scope.setFingerprint([
            'api-network-error',
            error.name,
            error.method ?? 'no-method',
            requestUri,
          ]);
        }

        if (error instanceof Error) {
          sentryCaptureException(error);
        } else {
          // Shouldn't happen, unless someone incorreclty tried to pass in a non-Error.
          sentryCaptureException(new Error(`Expected error, but got: ${JSON.stringify(error)}`));
        }
      });
      break;
    }
    case 'development':
      console.error('Captured exception', error, errorInfo);
      if (level === 'error') {
        throw error;
      }
  }
}

export function setLocationForReporting(pathname: string) {
  if (import.meta.env.PROD) {
    addBreadcrumb({
      message: 'Changed navigation path',
      data: {
        pathname,
      },
    });
  }
}
